import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Camera, Check, Copy, Trash2, Upload, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import ProfileAvatar from "../components/ProfileAvatar";

const MAX_AVATAR_SIZE = 120 * 1024 * 1024;
const MAX_STORED_AVATAR_SIZE = 4 * 1024 * 1024;
const MAX_AVATAR_DIMENSION = 1024;
const AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("image_decode_failed"));
    };
    image.src = objectUrl;
  });
}

async function optimizeAvatar(file) {
  let source;
  let dispose = () => {};
  if (typeof createImageBitmap === "function") {
    try {
      source = await createImageBitmap(file);
      dispose = () => source.close?.();
    } catch {
      source = await loadImage(file);
    }
  } else {
    source = await loadImage(file);
  }

  try {
    const originalWidth = source.width || source.naturalWidth;
    const originalHeight = source.height || source.naturalHeight;
    if (!originalWidth || !originalHeight) throw new Error("image_decode_failed");

    for (let dimension = MAX_AVATAR_DIMENSION; dimension >= 384; dimension = Math.floor(dimension * 0.75)) {
      const scale = Math.min(1, dimension / Math.max(originalWidth, originalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(originalWidth * scale));
      canvas.height = Math.max(1, Math.round(originalHeight * scale));
      const context = canvas.getContext("2d", { alpha: true });
      if (!context) throw new Error("canvas_unavailable");
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.82));
      if (blob?.type === "image/webp" && blob.size <= MAX_STORED_AVATAR_SIZE) {
        return new File([blob], "avatar.webp", { type: "image/webp", lastModified: Date.now() });
      }
    }
    throw new Error("image_compression_failed");
  } finally {
    dispose();
  }
}

function getOwnedAvatarPath(url, userId) {
  if (!url || !userId) return null;
  try {
    const pathname = new URL(url).pathname;
    const marker = "/storage/v1/object/public/avatars/";
    const markerIndex = pathname.indexOf(marker);
    if (markerIndex < 0) return null;
    const path = decodeURIComponent(pathname.slice(markerIndex + marker.length));
    return path.startsWith(`${userId}/`) ? path : null;
  } catch {
    return null;
  }
}

function getErrorDetail(error) {
  const code = error?.code || error?.statusCode || error?.status;
  const message = error?.message || error?.error_description;
  return [code, message].filter(Boolean).join(" — ");
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, refreshProfile, avatarFeaturesReady } = useAuth();
  const navigate = useNavigate();
  const { profileId } = useParams();
  const isOwnProfile = !profileId || profileId === user?.id;
  const fileInputRef = useRef(null);
  const avatarCheckAttemptedFor = useRef(null);
  const [publicProfile, setPublicProfile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [committedAvatarUrl, setCommittedAvatarUrl] = useState(undefined);
  const [loading, setLoading] = useState(!isOwnProfile);
  const [preparing, setPreparing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [nickname, setNickname] = useState(user?.full_name || user?.name || "");
  const [savingNickname, setSavingNickname] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const profile = isOwnProfile ? user : publicProfile;

  useEffect(() => {
    setNickname(profile?.full_name || profile?.name || "");
  }, [profile?.full_name, profile?.name]);

  useEffect(() => {
    if (isOwnProfile) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setError("");
    if (!supabase || !profileId) {
      setPublicProfile(null);
      setLoading(false);
      return undefined;
    }
    Promise.all([
      supabase.rpc("get_public_profiles", { profile_ids: [profileId] }),
      supabase.rpc("get_public_guides"),
    ]).then(([profileResult, guidesResult]) => {
      if (!active) return;
      if (guidesResult.data?.some((guide) => guide.id === profileId)) {
        navigate(`/guides/${profileId}`, { replace: true });
        return;
      }
      if (profileResult.error) setError(t("profile.load_error"));
      setPublicProfile(profileResult.data?.[0] || null);
      setLoading(false);
    });
    return () => { active = false; };
  }, [isOwnProfile, navigate, profileId, t]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    if (!isOwnProfile || !user?.id || avatarFeaturesReady || avatarCheckAttemptedFor.current === user.id) return;
    avatarCheckAttemptedFor.current = user.id;
    refreshProfile().catch(() => {});
  }, [avatarFeaturesReady, isOwnProfile, refreshProfile, user?.id]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setNotice("");
    if (!AVATAR_TYPES.has(file.type)) {
      setError(t("profile.file_type_error"));
      return;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      setError(t("profile.file_size_error"));
      return;
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setPreparing(true);
    try {
      const optimizedFile = await optimizeAvatar(file);
      setSelectedFile(optimizedFile);
      setPreviewUrl(URL.createObjectURL(optimizedFile));
    } catch {
      setError(t("profile.optimize_error"));
    } finally {
      setPreparing(false);
    }
  };

  const saveAvatar = async () => {
    if (!user || !selectedFile || !supabase) return;
    setSaving(true);
    setError("");
    setNotice("");
    const previousPath = getOwnedAvatarPath(user.avatar_url, user.id);
    const path = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.webp`;
    let uploaded = false;
    let profileSaved = false;
    try {
      const { error: uploadError } = await supabase.storage.from("avatars").upload(path, selectedFile, {
        cacheControl: "3600",
        contentType: selectedFile.type,
        upsert: false,
      });
      if (uploadError) throw uploadError;
      uploaded = true;

      const { data: publicUrlData } = supabase.storage.from("avatars").getPublicUrl(path);
      const avatarUrl = publicUrlData?.publicUrl;
      if (!avatarUrl) throw new Error("Supabase did not return a public avatar URL.");

      const { data: updatedProfile, error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: avatarUrl })
        .eq("id", user.id)
        .select("id")
        .maybeSingle();
      if (updateError) throw updateError;
      if (!updatedProfile) throw new Error("The profile row was not updated. Check the profile update policy.");
      profileSaved = true;
      setCommittedAvatarUrl(avatarUrl);

      if (previousPath) {
        await supabase.storage.from("avatars").remove([previousPath]);
      }
      await refreshProfile().catch(() => null);
      setSelectedFile(null);
      setPreviewUrl(null);
      setNotice(t("profile.saved"));
    } catch (saveError) {
      if (uploaded && !profileSaved) {
        try {
          await supabase.storage.from("avatars").remove([path]);
        } catch {
          // Keep the original save error visible; cleanup can be retried separately.
        }
      }
      const detail = getErrorDetail(saveError);
      setError(`${t(uploaded ? "profile.save_error" : "profile.upload_error")}${detail ? ` (${detail})` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  const removeAvatar = async () => {
    if (!user || !user.avatar_url || !supabase) return;
    if (!window.confirm(t("profile.remove_confirm"))) return;
    setSaving(true);
    setError("");
    setNotice("");
    const previousPath = getOwnedAvatarPath(user.avatar_url, user.id);
    try {
      const { data: updatedProfile, error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("id", user.id)
        .select("id")
        .maybeSingle();
      if (updateError) throw updateError;
      if (!updatedProfile) throw new Error("The profile row was not updated. Check the profile update policy.");
      setCommittedAvatarUrl(null);

      if (previousPath) await supabase.storage.from("avatars").remove([previousPath]);
      await refreshProfile().catch(() => null);
      setNotice(t("profile.removed"));
    } catch (removeError) {
      const detail = getErrorDetail(removeError);
      setError(`${t("profile.save_error")}${detail ? ` (${detail})` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  const saveNickname = async (event) => {
    event.preventDefault();
    const fullName = nickname.trim();
    if (!user || !supabase || fullName.length < 2) {
      setError(t("profile.nickname_invalid"));
      return;
    }
    if (fullName === user.full_name) return;

    setSavingNickname(true);
    setError("");
    setNotice("");
    try {
      const { data: updatedProfile, error: updateError } = await supabase
        .from("profiles")
        .update({ full_name: fullName })
        .eq("id", user.id)
        .select("id")
        .maybeSingle();
      if (updateError) throw updateError;
      if (!updatedProfile) throw new Error("The profile row was not updated.");
      await refreshProfile();
      setNickname(fullName);
      setNotice(t("profile.nickname_saved"));
    } catch (saveError) {
      const isPermissionError = saveError?.code === "42501";
      setError(isPermissionError ? t("profile.nickname_setup_error") : t("profile.nickname_save_error"));
    } finally {
      setSavingNickname(false);
    }
  };

  const copyProfileLink = async () => {
    if (!profile?.id) return;
    try {
      const profilePath = profile.role === "guide" ? `/guides/${profile.id}` : `/profile/${profile.id}`;
      await navigator.clipboard.writeText(`${window.location.origin}${profilePath}`);
      setNotice(t("profile.link_copied"));
    } catch {
      setError(t("profile.copy_error"));
    }
  };

  if (loading) {
    return <main className="min-h-[calc(100svh-68px)] bg-[#F7F6F3] px-4 py-16 text-center text-slate-500 dark:bg-slate-950 dark:text-slate-400">{t("profile.loading")}</main>;
  }

  if (!profile) {
    return <main className="min-h-[calc(100svh-68px)] bg-[#F7F6F3] px-4 py-16 text-center text-slate-600 dark:bg-slate-950 dark:text-slate-300">{error || t("profile.not_found")}</main>;
  }

  const avatarUrl = previewUrl || (committedAvatarUrl !== undefined ? committedAvatarUrl : profile.avatar_url);

  return (
    <main className="min-h-[calc(100svh-68px)] bg-[#F7F6F3] px-4 py-10 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:px-6 sm:py-14">
      <div className="mx-auto max-w-2xl">
        <section className="relative rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-9">
          {isOwnProfile && avatarFeaturesReady && <button type="button" onClick={copyProfileLink} aria-label={t("profile.copy_link")} title={t("profile.copy_link")} className="absolute left-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-600 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:border-slate-700 dark:bg-slate-800/90 dark:text-slate-200 dark:hover:border-amber-400/50 dark:hover:bg-slate-700 sm:left-6 sm:top-6">
            <Copy className="h-4 w-4" aria-hidden="true" />
          </button>}
          {isOwnProfile && avatarFeaturesReady && profile.avatar_url && !selectedFile && <button type="button" disabled={saving || savingNickname || preparing} onClick={removeAvatar} aria-label={t("profile.remove_avatar")} title={t("profile.remove_avatar")} className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-red-200 bg-white/90 text-red-500 shadow-sm transition hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:border-red-400/30 dark:bg-slate-800/90 dark:text-red-300 dark:hover:bg-red-400/10 sm:right-6 sm:top-6">
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>}
          <div className="flex flex-col items-center text-center">
            <div className="relative">
              <ProfileAvatar avatarUrl={avatarUrl} name={profile.full_name || profile.name} className="h-28 w-28 ring-4 ring-amber-50 dark:ring-slate-800" textClassName="text-3xl" />
              {isOwnProfile && <span className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full border-4 border-white bg-amber-400 text-slate-950 dark:border-slate-900"><Camera className="h-4 w-4" /></span>}
            </div>
            <p className="mt-5 text-xs font-extrabold uppercase tracking-[.18em] text-amber-600 dark:text-amber-400">{isOwnProfile ? t("profile.title") : t("profile.public_title")}</p>
            <h1 className="mt-2 text-2xl font-extrabold sm:text-3xl">{profile.full_name || profile.name}</h1>
            <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">{isOwnProfile ? t("profile.visibility_hint") : t("profile.public_hint")}</p>
            {isOwnProfile && <form onSubmit={saveNickname} className="mt-5 flex w-full max-w-md flex-col gap-2 sm:flex-row">
              <label htmlFor="profile-nickname" className="sr-only">{t("profile.nickname_label")}</label>
              <input id="profile-nickname" type="text" required minLength={2} maxLength={60} value={nickname} onChange={(event) => setNickname(event.target.value)} placeholder={t("profile.nickname_placeholder")} disabled={saving || savingNickname} className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-white" />
              <button type="submit" disabled={saving || savingNickname || nickname.trim().length < 2 || nickname.trim() === user.full_name} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-amber-400 dark:text-slate-950 dark:hover:bg-amber-300">
                <Check className="h-4 w-4" aria-hidden="true" />{savingNickname ? t("profile.nickname_saving") : t("profile.nickname_save")}
              </button>
            </form>}
          </div>

          {error && <p role="alert" className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-200">{error}</p>}
          {notice && <p role="status" className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-200"><Check className="h-4 w-4" />{notice}</p>}

          {isOwnProfile ? <div className="mt-7 space-y-3">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={handleFileChange} disabled={preparing} className="sr-only" />
            <button type="button" disabled={preparing} onClick={() => fileInputRef.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-3 font-bold text-slate-950 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50">
              <Upload className="h-4 w-4" />{preparing ? t("profile.processing") : profile.avatar_url ? t("profile.change_avatar") : t("profile.upload_avatar")}
            </button>
            {selectedFile && <div className="flex flex-col gap-2 sm:flex-row">
              <button type="button" disabled={saving} onClick={saveAvatar} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-3 font-bold text-slate-950 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60">
                <Check className="h-4 w-4" />{saving ? t("profile.saving") : t("profile.save_avatar")}
              </button>
              <button type="button" disabled={saving} onClick={() => { setSelectedFile(null); setPreviewUrl(null); }} className="rounded-xl border border-slate-200 px-4 py-3 font-semibold text-slate-600 dark:border-slate-700 dark:text-slate-300">{t("profile.cancel")}</button>
            </div>}
            {!avatarFeaturesReady && <p role="status" className="rounded-xl bg-amber-50 px-4 py-3 text-center text-sm text-amber-900 dark:bg-amber-400/10 dark:text-amber-100">{t("profile.setup_pending")}</p>}
          </div> : <Link to="/" className="mx-auto mt-7 flex w-fit items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 font-semibold text-slate-700 dark:border-slate-700 dark:text-slate-200">
            <UserRound className="h-4 w-4" />{t("profile.back_home")}
          </Link>}
        </section>
      </div>
    </main>
  );
}
